<template>
  <div
    v-if="creativeFile !== null"
    class="creative-preview"
    :class="size">
    <!-- CREATIVE HAS A FILE BUT CAN'T BE LOADED -->
    <creative-preview-error v-if="isError" />

    <template v-else-if="creativeFile.isImage">
      <img
        v-show="!isLoading"
        :src="url"
        :alt="name"
        @load="loaded"
        @error="onError"
        @click="onCreativeThumbnailClicked" />
      <loader-small v-if="isLoading" />
    </template>

    <template v-else-if="creativeFile.isVideo">
      <div
        v-if="isShowingPlayButton"
        class="play-button">
        <icon-colored-play />
      </div>
      <video
        v-show="!isLoading"
        loop="true"
        :src="url"
        @loadeddata="loaded"
        @error="onError"
        @click="onCreativePreviewClicked"
        @mouseover="playVideo"
        @mouseleave="pauseVideo" />
      <loader-small v-if="isLoading" />
    </template>

    <iframe
      v-else-if="showHtmlPreview && creativeFile.isHtml"
      title="preview"
      :src="creativeFile.url"
      frameborder="0" />

    <icon-colored-html v-else-if="creativeFile.isHtml" />

    <template v-else-if="creativeFile.isVast">
      <icon-colored-vast-tag class="vast-tag" />
    </template>

    <icon-colored-zip v-else-if="creativeFile.isZip" />

    <icon-colored-audio v-else-if="creativeFile.isAudio" />

    <span v-else>{{ creativeFile.format }}</span>
  </div>

  <!-- CREATIVE HAS NO FILE TO DISPLAY -->
  <div
    v-else
    class="creative-preview">
    <icon-colored-creative-file :title="capitalize($t('messages.tablePlaceholderNoCreativeFilesTitle'))" />
  </div>

  <full-screen-image
    :is-visible="isCreativeThumbnailModalVisible"
    :image-url="url"
    :name="name"
    @close="() => (isCreativeThumbnailModalVisible = false)" />

  <full-screen-video
    :is-visible="isCreativePreviewModalVisible"
    :video-url="url"
    :name="name"
    @close="() => (isCreativePreviewModalVisible = false)" />
</template>

<script lang="ts" setup>
  import { capitalize, computed, ref } from 'vue';

  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import {
    IconColoredAudio,
    IconColoredCreativeFile,
    IconColoredHtml,
    IconColoredPlay,
    IconColoredVastTag,
    IconColoredZip,
  } from '@/common/icons/colored';
  import { CreativeFile } from '@/common/models';
  import FullScreenImage from '@/org-app/components/controls/FullScreenImage.vue';
  import FullScreenVideo from '@/org-app/components/controls/FullScreenVideo.vue';
  import i18n from '@/vueI18n';

  import CreativePreviewError from './CreativePreviewError.vue';

  interface IProps {
    creativeFile: CreativeFile | null;
    autoPlay?: boolean;
    canPause?: boolean;
    showHtmlPreview?: boolean;
    showPlayButtonForVideo?: boolean;
    size?: string;
    useEvents?: boolean;
    useUrlAsThumbnail?: boolean;
  }

  const errorMessageKey = 'messages.mediaFileNotLoading';

  const {
    creativeFile = null,
    autoPlay = false,
    canPause = true,
    showHtmlPreview = false,
    showPlayButtonForVideo = false,
    size = 'md',
    useEvents = true,
    useUrlAsThumbnail = false,
  } = defineProps<IProps>();

  const emits = defineEmits(['errored', 'loaded', 'previewClicked', 'thumbnailClicked']);

  const isCreativePreviewModalVisible = ref<boolean>(false);
  const isCreativeThumbnailModalVisible = ref<boolean>(false);
  const isError = ref<boolean>(false);
  const isLoading = ref<boolean>(true);
  const isShowingPlayButton = ref<boolean>(false);

  const url = computed<string>(() => {
    if (!creativeFile) return null;

    if (useUrlAsThumbnail) {
      return creativeFile.url;
    }

    if (creativeFile.isVideo) {
      return creativeFile.videoPreviewUrl;
    }

    return creativeFile.imageThumbnailUrl;
  });
  const name = computed<string>(() => creativeFile?.name);

  function playVideo(e) {
    if (showPlayButtonForVideo) {
      isShowingPlayButton.value = false;
    }

    e.target.play();
  }

  function pauseVideo(e) {
    if (!canPause) return;

    if (showPlayButtonForVideo) {
      isShowingPlayButton.value = true;
    }

    e.target.pause();
  }

  function loaded(e: Event) {
    isLoading.value = false;

    if (e.target instanceof HTMLVideoElement && autoPlay) {
      playVideo(e);
    }

    emits('loaded', e);
  }

  function onError() {
    isError.value = true;
    emits('errored', { error: i18n.global.t(errorMessageKey), creativeFile });
  }

  function onCreativeThumbnailClicked(e) {
    if (!useEvents) {
      isCreativeThumbnailModalVisible.value = true;
      return;
    }

    emits('thumbnailClicked', e);
  }

  function onCreativePreviewClicked(e) {
    if (!useEvents) {
      isCreativePreviewModalVisible.value = true;
      return;
    }

    emits('previewClicked', e);
  }
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  .creative-preview {
    display: inline-block;
    position: relative;

    img,
    video {
      cursor: pointer;
    }

    &.flex {
      img,
      video {
        max-height: 6.4rem;
      }

      svg {
        width: 6.4rem;
        height: 6.4rem;
        stroke-width: 0.7px !important;
      }
    }

    &.full {
      img,
      video {
        height: 100%;
        width: 100%;
      }
    }

    .play-button {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.5);
      width: 100%;
      height: 100%;
    }

    &.auto {
      img,
      video {
        height: auto;
        width: 100%;
      }
    }

    &.sm {
      img,
      video {
        height: 3.2rem !important;
        max-width: 6.4rem;
        margin: 0.1rem 0 !important;
      }
    }

    &.md {
      img,
      video {
        max-height: 4.8rem !important;
        margin: 0.1rem 0 !important;
      }
    }

    &.lg {
      img,
      video {
        max-height: 9.6rem !important;
        margin: 0.1rem 0 !important;
      }
    }

    &.lg-fixed {
      img,
      video {
        height: 9.6rem !important;
        margin: 0.1rem 0 !important;
      }
    }

    &.xl {
      img,
      video {
        max-height: 12.8rem !important;
        margin: 0.1rem 0 !important;
      }
    }

    &.xl-fixed {
      img,
      video {
        height: 12.8rem !important;
        margin: 0 1.6rem 1.6rem 0 !important;
        box-shadow: $big-shadow;
      }
    }

    &.xxl {
      img,
      video {
        max-height: 25.6rem !important;
        margin: 0.1rem 0 !important;
      }
    }

    &.xxl-fixed {
      img,
      video {
        height: 25.6rem !important;
        margin: 0 1.6rem 1.6rem 0 !important;
        box-shadow: $big-shadow;
      }
    }

    span {
      color: var(--textMutedColor);
      display: block;
      text-align: center;
      font-size: 0.9em;
      border-radius: 1.6rem;
      padding: 0.4rem 0.8rem;
      font-style: italic;
    }

    svg {
      height: 4.8rem;
    }
  }
</style>
